import React, { useState, useRef, useEffect, useCallback } from "react";
import { Pressable, TextInput, View, Keyboard } from "react-native";
import color from "@constants/color";
import Text from "@components/CustomText";
import toast from "@utils/toast";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import {
  sendOtp,
  verifyOtp,
  getOnboardingStatus,
  getBlockedErrorMessage,
} from "@services/onboardingApi";
import { useOnboarding } from "./OnboardingContext";
import { useFlowConfig } from "./FlowConfigContext";
import { useActiveFlow } from "./ActiveFlowContext";
import { otpStyles as styles } from "../Login/styles";
import type { StackNavigationProp } from "@react-navigation/stack";
import type { RouteProp } from "@react-navigation/native";
import type { OnboardingStackParamList } from "./types";

const OTP_LENGTH = 4;
const COUNTDOWN_SECONDS = 90;

interface OnboardingOTPProps {
  navigation: StackNavigationProp<OnboardingStackParamList, "OnboardingOTP">;
  route: RouteProp<OnboardingStackParamList, "OnboardingOTP">;
}

const OnboardingOTP: React.FC<OnboardingOTPProps> = ({ navigation, route }) => {
  const { phoneNumber, skipKyc = false } = route.params;
  const { data: onboardingData, updateData } = useOnboarding();
  const { flowConfig, setFlowConfig } = useFlowConfig();
  const { setActiveFlow } = useActiveFlow();
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [isVerifying, setIsVerifying] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const inputRefs = useRef<(TextInput | null)[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const otpValue = otp.join("");
  const canResend = countdown === 0;

  useEffect(() => {
    sendOtp(phoneNumber).catch(() => {
      toast.showLongToast("Failed to send OTP. Tap Resend to retry.");
    });
    startCountdown();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCountdown = useCallback(() => {
    setCountdown(COUNTDOWN_SECONDS);
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }, []);

  const handleOtpChange = useCallback(
    (text: string, index: number) => {
      const digit = text.replace(/[^\d]/g, "");

      if (digit.length > 1) {
        const digits = digit.slice(0, OTP_LENGTH).split("");
        const newOtp = [...otp];
        digits.forEach((d, i) => {
          if (index + i < OTP_LENGTH) {
            newOtp[index + i] = d;
          }
        });
        setOtp(newOtp);
        const nextIndex = Math.min(index + digits.length, OTP_LENGTH - 1);
        inputRefs.current[nextIndex]?.focus();
        return;
      }

      const newOtp = [...otp];
      newOtp[index] = digit;
      setOtp(newOtp);

      if (digit && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [otp],
  );

  const handleKeyPress = useCallback(
    (key: string, index: number) => {
      if (key === "Backspace" && !otp[index] && index > 0) {
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      }
    },
    [otp],
  );

  const verifyOtpAndNavigate = useCallback(async () => {
    if (isVerifying) return;

    setIsVerifying(true);
    Keyboard.dismiss();

    try {
      const result = await verifyOtp(phoneNumber, otpValue, {
        convertFromCp: onboardingData.isCpConversion,
      });

      // Merge returned flow_config into context (falls back to default if absent)
      const activeCfg = result.flowConfig ?? flowConfig;
      if (result.flowConfig) setFlowConfig(result.flowConfig);

      // skipKyc path — already KYC-verified, go straight to spoke selection
      if (skipKyc) {
        navigation.navigate("NearestHub");
        return;
      }

      // Fetch resume state to detect partially-completed sessions
      let resumeStep = "fresh";
      let dbType: "NORMAL" | "LAGGAN" | null = null;
      let statusData: Awaited<ReturnType<typeof getOnboardingStatus>> | null =
        null;
      try {
        statusData = await getOnboardingStatus();
        if (statusData?.resume_step) resumeStep = statusData.resume_step;
        if (statusData?.db_type) dbType = statusData.db_type;
      } catch {
        // Non-fatal — treat as fresh session
      }

      if (resumeStep === "ApplicationSubmitted") {
        navigation.navigate("ApplicationSubmitted", {
          spokeName: statusData?.spoke_name ?? "",
          spokeAddress: statusData?.spoke_address ?? "",
          isReturning: true,
          kycStatus: statusData?.kyc_status ?? null,
        });
        return;
      }

      if (resumeStep === "SignUpSuccessful") {
        const dbTypeId = dbType ?? "NORMAL";
        const dbTypeCfg = activeCfg.db_types.find((t) => t.id === dbTypeId);
        if (dbTypeCfg) setActiveFlow(dbTypeCfg.id, dbTypeCfg.steps);
        const riderType = dbType === "LAGGAN" ? "laggan" : "normal";
        updateData({ phoneNumber, riderType });
        navigation.navigate("SignUpSuccessful");
        return;
      }

      if (resumeStep === "PersonalDetails") {
        const dbTypeId = dbType ?? "NORMAL";
        const dbTypeCfg = activeCfg.db_types.find((t) => t.id === dbTypeId);
        if (dbTypeCfg) setActiveFlow(dbTypeCfg.id, dbTypeCfg.steps);
        const riderType = dbType === "LAGGAN" ? "laggan" : "normal";
        updateData({ phoneNumber, riderType });
        navigation.navigate("PersonalDetails");
        return;
      }

      // Re-onboarding: KYC already VERIFIED but cp_dbs got deactivated.
      // User needs to pick a new spoke; skip the full KYC flow.
      if (resumeStep === "SpokeSelection") {
        const dbTypeId = dbType ?? "NORMAL";
        const dbTypeCfg = activeCfg.db_types.find((t) => t.id === dbTypeId);
        if (dbTypeCfg) setActiveFlow(dbTypeCfg.id, dbTypeCfg.steps);
        const riderType = dbType === "LAGGAN" ? "laggan" : "normal";
        updateData({ phoneNumber, riderType });
        navigation.navigate("NearestHub");
        return;
      }

      // Fresh new user — phone number stored, go pick DB type
      updateData({ phoneNumber });
      navigation.navigate("RiderType");
    } catch (err: unknown) {
      const blockedMessage = getBlockedErrorMessage(err);
      const axiosErr = err as { response?: { data?: { message?: string } } };
      toast.showLongToast(
        blockedMessage ||
          axiosErr?.response?.data?.message ||
          "Invalid OTP. Please try again.",
      );
    } finally {
      setIsVerifying(false);
    }
  }, [
    isVerifying,
    phoneNumber,
    otpValue,
    skipKyc,
    onboardingData.isCpConversion,
    flowConfig,
    setFlowConfig,
    setActiveFlow,
    updateData,
    navigation,
  ]);

  const resendOtp = useCallback(async () => {
    if (!canResend) return;

    try {
      await sendOtp(phoneNumber);
      startCountdown();
      toast.showLongToast("OTP sent successfully");
    } catch {
      toast.showLongToast("Failed to resend OTP. Please try again.");
    }
  }, [canResend, phoneNumber, startCountdown]);

  return (
    <View style={styles.container}>
      <HeaderWithBackAndTitle
        headertitle="Verify OTP"
        navigation={navigation}
        hideShadow
      />

      <View style={styles.contentContainer}>
        <Text style={styles.title}>Create New Account</Text>
        <Text variant="body_text_3" style={styles.subtitle}>
          Enter the OTP sent to your mobile number
        </Text>
        <Text style={styles.phoneNumber}>+91 {phoneNumber}</Text>

        <View style={styles.otpContainer}>
          {otp.map((digit, index) => (
            <TextInput
              key={index}
              ref={(ref) => {
                inputRefs.current[index] = ref;
              }}
              style={[
                styles.otpInput,
                focusedIndex === index && styles.otpInputFocused,
                digit ? styles.otpInputFilled : undefined,
              ]}
              value={digit}
              maxLength={index === 0 ? OTP_LENGTH : 1}
              keyboardType="number-pad"
              onChangeText={(text) => handleOtpChange(text, index)}
              onKeyPress={({ nativeEvent }) =>
                handleKeyPress(nativeEvent.key, index)
              }
              onFocus={() => setFocusedIndex(index)}
              autoFocus={index === 0}
            />
          ))}
        </View>

        <View style={styles.timerContainer}>
          {canResend ? (
            <Pressable
              onPress={resendOtp}
              accessibilityRole="button"
              accessibilityLabel="Resend OTP"
            >
              <Text style={styles.resendText}>Resend</Text>
            </Pressable>
          ) : (
            <Text style={styles.timerText}>{formatTime(countdown)}</Text>
          )}
        </View>

        <Pressable
          style={[styles.verifyButton, { backgroundColor: color.primary }]}
          disabled={isVerifying}
          onPress={verifyOtpAndNavigate}
          accessibilityRole="button"
          accessibilityLabel="Verify OTP"
        >
          <Text variant="title_16" style={styles.verifyButtonText}>
            Verify
          </Text>
        </Pressable>
      </View>
    </View>
  );
};

export default OnboardingOTP;
